/**
 * Развёрнутая форма одной должности профрисков в списке шага «Должности»
 * (centrika-docs!26, `component.create-profrisk-order.positions`, разделы «Карточка должности»,
 * «Поля формы», «Подсветка незаполненной формы»). Отдельного экрана карточки больше нет —
 * все должности заказа показаны формами одна под другой прямо на шаге.
 *
 * Заголовок: порядковый номер в круглом индикаторе, наименование (или «Должность не указана»),
 * метка источника («из структуры» / «вручную»; пустой источник метки не даёт — design D1) и
 * кнопка «Убрать» (без подтверждения). Поля — двумя строками: подразделение / должность /
 * штатных единиц, затем выезды на служебном авто / оборудование.
 *
 * Подсветка: пока не заполнено хотя бы одно обязательное поле, ВСЯ карточка получает светлый
 * предупреждающий фон и оранжевую рамку (`border-amber bg-amber-bg`, как строка рабочего места
 * СОУТ) — не красную. Снимается, как только все обязательные поля заполнены.
 *
 * Полностью контролируемая: любой ввод поднимается через `onChange` — родитель автосохраняет
 * (updatePosition). Служебное `cardsCount` клиенту НЕ показывается. В режиме просмотра
 * (`readOnly`) поля недоступны, список подразделений не открывается, кнопки «Убрать» нет.
 */
import { cn } from '@/lib/utils'
import type { Department, ProfriskPosition } from '@/lib/api'
import { VEHICLE_USE_OPTIONS, isPositionValid } from '@/lib/profrisk'
import {
  PROFRISK_POSITION_FIELDS,
  PROFRISK_POSITION_PLACEHOLDERS,
  PROFRISK_POSITION_REMOVE,
  PROFRISK_POSITION_SOURCE_LABELS,
  PROFRISK_POSITION_UNNAMED,
} from './profrisk-copy'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { DepartmentSelect } from '@/company/department-select'
import { PositionSelect } from '@/company/position-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:bg-subtle disabled:opacity-50'

/** Пустое поле вместо нуля: штатные единицы клиент всегда вводит вручную (0 = «не заполнено»). */
function numValue(n: number): string {
  return n > 0 ? String(n) : ''
}

export interface PositionFormProps {
  position: ProfriskPosition
  /** Сквозной порядковый номер формы в списке шага. */
  index: number
  readOnly: boolean
  /** Актуальные подразделения структуры — общий список всех форм шага. */
  departments: Department[]
  /** Идёт мутация списка — «Убрать» заблокирована. */
  busy?: boolean
  onChange: (next: ProfriskPosition) => void
  onRemove: () => void
  /** Создание подразделения прямо в выпадающем списке (см. DepartmentSelect). */
  onCreateDepartment: (name: string) => Promise<Department>
  /** Текст ошибки создания подразделения по ответу сервера. */
  departmentCreateError: (err: unknown) => string
}

export function PositionForm({
  position,
  index,
  readOnly,
  departments,
  busy,
  onChange,
  onRemove,
  onCreateDepartment,
  departmentCreateError,
}: PositionFormProps) {
  const valid = isPositionValid(position)

  function patch(p: Partial<ProfriskPosition>) {
    onChange({ ...position, ...p })
  }

  return (
    <div
      className={cn(
        'rounded-12 border p-4',
        valid || readOnly ? 'border-line bg-white' : 'border-amber bg-amber-bg',
      )}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-bg text-13 font-semibold text-accent-txt">
          {index}
        </span>
        <span className="font-semibold text-txt">
          {position.position || PROFRISK_POSITION_UNNAMED}
        </span>
        {/* Метка источника: пустой источник — должность создана до centrika-docs!26,
            способ добавления не известен и метку не рисуем (design D1). */}
        {position.source === 'structure' ? (
          <Badge variant="blue">{PROFRISK_POSITION_SOURCE_LABELS.structure}</Badge>
        ) : null}
        {position.source === 'manual' ? (
          <Badge variant="gray">{PROFRISK_POSITION_SOURCE_LABELS.manual}</Badge>
        ) : null}
        {position.source === 'import' ? (
          <Badge variant="green">{PROFRISK_POSITION_SOURCE_LABELS.import}</Badge>
        ) : null}
        {!readOnly ? (
          <Button
            className="ml-auto"
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={onRemove}
          >
            {PROFRISK_POSITION_REMOVE}
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 nw:grid-cols-3">
        <Field label={PROFRISK_POSITION_FIELDS.department} required={!readOnly} className="mb-0">
          {(props) => (
            <DepartmentSelect
              id={props.id}
              aria-required={props['aria-required']}
              value={position.departmentName}
              departments={departments}
              disabled={readOnly}
              placeholder={PROFRISK_POSITION_PLACEHOLDERS.department}
              onSelect={(name) => patch({ departmentName: name })}
              // В режиме просмотра действие «+ Добавить новое подразделение» недоступно.
              onCreate={readOnly ? undefined : onCreateDepartment}
              createErrorText={departmentCreateError}
            />
          )}
        </Field>
        <Field label={PROFRISK_POSITION_FIELDS.position} required={!readOnly} className="mb-0">
          {(props) => (
            <PositionSelect
              {...props}
              // Выбор из справочника ИЛИ свой текст (position-free-text): positionId авторитетен
              // для матчинга результатов к структуре, но должность — свободное текстовое поле, и
              // введённая вручную заводится вместе с сохранением должности анкеты. position —
              // замороженный лейбл снапшота. Подсказку в поле задаёт сам PositionSelect
              // (единый текст режима свободного ввода, centrika-docs!96).
              value={position.positionId}
              valueName={position.position}
              disabled={readOnly}
              allowCreate
              onChange={(id, name) => patch({ position: name, positionId: id })}
            />
          )}
        </Field>
        <Field label={PROFRISK_POSITION_FIELDS.headcount} required={!readOnly} className="mb-0">
          {(props) => (
            <Input
              {...props}
              type="number"
              min={1}
              value={numValue(position.headcount)}
              disabled={readOnly}
              onChange={(e) => patch({ headcount: Number(e.target.value) || 0 })}
            />
          )}
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-3 nw:grid-cols-2">
        <Field label={PROFRISK_POSITION_FIELDS.vehicleUse} required={!readOnly} className="mb-0">
          {(props) => (
            <Select
              value={position.vehicleUse}
              onValueChange={(v) => patch({ vehicleUse: v as ProfriskPosition['vehicleUse'] })}
              disabled={readOnly}
            >
              <SelectTrigger
                id={props.id}
                aria-label={PROFRISK_POSITION_FIELDS.vehicleUse}
                aria-required={props['aria-required']}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VEHICLE_USE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label={PROFRISK_POSITION_FIELDS.equipment} className="mb-0">
          {(props) => (
            <textarea
              {...props}
              value={position.equipment}
              disabled={readOnly}
              rows={3}
              className={TEXTAREA_CLASS}
              placeholder={PROFRISK_POSITION_PLACEHOLDERS.equipment}
              onChange={(e) => patch({ equipment: e.target.value })}
            />
          )}
        </Field>
      </div>
    </div>
  )
}
